import { IconType } from '@cognite/cogs.js';
import { Milestone, Rule, RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { compareRunDates, sortRunsByDate } from 'utils/data-validation';
import { compareISODates } from 'utils/misc';

export const calculateSuccessRate = (valid: number, failed: number): number => {
  const total = valid + failed;
  return total > 0 ? Math.round((valid / total) * 100) : 0;
};

export type Collection = Milestone & { reportId: string };

type SortType =
  | 'milestoneNameASC'
  | 'milestoneNameDESC'
  | 'runTimeASC'
  | 'runTimeDESC'
  | 'activeTimeASC'
  | 'activeTimeDESC';

type OptionType = { icon: IconType; name: string; value: SortType };

export const sortOptions: OptionType[] = [
  /* The icons SortDescending and SortAscending are reversed for some reason. 
  That's why SortDescending had be used in place of SortAscending, and so on.  */
  {
    icon: 'SortDescending',
    name: 'Milestone name (ASC)',
    value: 'milestoneNameASC',
  },
  {
    icon: 'SortAscending',
    name: 'Milestone name (DESC)',
    value: 'milestoneNameDESC',
  },
  {
    icon: 'SortDescending',
    name: 'Run time (ASC)',
    value: 'runTimeASC',
  },
  {
    icon: 'SortAscending',
    name: 'Run time (DESC)',
    value: 'runTimeDESC',
  },
  {
    icon: 'SortDescending',
    name: 'Active time period (ASC)',
    value: 'activeTimeASC',
  },
  {
    icon: 'SortAscending',
    name: 'Active time period (DESC)',
    value: 'activeTimeDESC',
  },
];

const getValidRulesForCollection = (collection: Milestone) =>
  (collection.rules?.items.filter(
    (rule) => rule!.ruleRunStatus!.items.length > 0
  ) as Rule[]) || [];

export const sortCollections = (
  sortOption: SortType,
  collections: Collection[]
) => {
  if (!collections) return [];

  const array = [...collections];

  switch (sortOption) {
    case 'milestoneNameASC':
      return array.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      );

    case 'milestoneNameDESC':
      return array.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? -1 : 1
      );

    case 'runTimeASC':
      return array.sort((a, b) => {
        const validRulesA: Rule[] = getValidRulesForCollection(a);
        const validRulesB: Rule[] = getValidRulesForCollection(b);

        const sortedRunsA = sortRunsByDate(
          (validRulesA![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
        );
        const sortedRunsB = sortRunsByDate(
          (validRulesB![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
        );

        return compareRunDates(sortedRunsB[0], sortedRunsA[0]);
      });

    case 'runTimeDESC':
      return array.sort((a, b) => {
        const validRulesA: Rule[] = getValidRulesForCollection(a);
        const validRulesB: Rule[] = getValidRulesForCollection(b);

        const sortedRunsA = sortRunsByDate(
          (validRulesA![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
        );
        const sortedRunsB = sortRunsByDate(
          (validRulesB![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
        );

        return compareRunDates(sortedRunsA[0], sortedRunsB[0]);
      });

    case 'activeTimeASC':
      return array.sort((a, b) =>
        compareISODates(a.startActiveTime || null, b.startActiveTime || null) &&
        compareISODates(a.endActiveTime || null, b.endActiveTime || null)
          ? -1
          : 1
      );

    case 'activeTimeDESC':
      return array.sort((a, b) =>
        compareISODates(a.startActiveTime || null, b.startActiveTime || null) &&
        compareISODates(a.endActiveTime || null, b.endActiveTime || null)
          ? 1
          : -1
      );

    default:
      return array.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      );
  }
};
